import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_STORAGE_KEY } from '../../../../i18n';

export type Language = 'RU' | 'ENG';

export interface LanguageSwitcherProps {
  variant: 'light' | 'dark';
  currentLanguage?: Language;
  onLanguageChange?: (language: Language) => void;
  className?: string;
}

const I18N_TO_UI: Record<string, Language> = { en: 'ENG', ru: 'RU' };
const UI_TO_I18N: Record<Language, string> = { ENG: 'en', RU: 'ru' };

export function LanguageSwitcher({
  variant,
  currentLanguage: controlledLanguage,
  onLanguageChange,
  className,
}: LanguageSwitcherProps) {
  const { i18n } = useTranslation();

  const active: Language = controlledLanguage ?? I18N_TO_UI[i18n.language?.slice(0, 2) ?? 'en'] ?? 'ENG';

  const handleLanguageChange = (newLang: Language) => {
    const lngCode = UI_TO_I18N[newLang];
    i18n.changeLanguage(lngCode);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lngCode);
    } catch {
      // ignore
    }
    onLanguageChange?.(newLang);
  };

  const containerClasses = clsx(
    'border border-solid p-[3px] rounded-[80px] text-sm font-semibold flex',
    {
      'border-[#C8CBCA] bg-[#EFE8DF]': variant === 'light',
      'border-[#C8CBCA] bg-transparent': variant === 'dark',
    },
    className
  );

  const getButtonClasses = (lang: Language) => {
    const isActive = active === lang;

    return clsx(
      'h-[29px] px-3 rounded-[70px] min-w-[48px] transition-colors flex-1',
      {
        'text-white bg-[#9B2626]': variant === 'light' && isActive,
        'text-[#7E8180]': variant === 'light' && !isActive,
        'text-[#4A4A4A] bg-[#EFE8DF]': variant === 'dark' && isActive,
        'text-[#C8CBCA]': variant === 'dark' && !isActive,
      }
    );
  };

  return (
    <div className={containerClasses}>
      <button
        className={getButtonClasses('RU')}
        onClick={() => handleLanguageChange('RU')}
      >
        RU
      </button>
      <button
        className={getButtonClasses('ENG')}
        onClick={() => handleLanguageChange('ENG')}
      >
        ENG
      </button>
    </div>
  );
}
